var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ServerTimeMessage, SessionDisconnectMessage } from "../../messages/session/SessionMessages";
import { ChangeRoomSupervisionMessage, DeleteModelMessage, NetworkFacet, RemoveSubModelMessage, UpdateFacetOnModelMessage, RemoveFacetFromModelMessage, RemoveFacetsOnDisconnectFacet, ClientReloadSessionMessage, } from "flavr-common";
import { busService } from "../bus";
import { coreUserService } from "../users";
import { modelPool } from "@modules/model";
class NetworkEntitiesService {
    constructor() {
        this.connection = busService.connection("NetworkEntitiesService");
        this.entities = new Map();
        this.peerIdxEntitiesMap = new Map();
        this.isRoomHost = false;
        this.avgTimeOffset = 0;
        this.onOwnerChangedCallbacks = new Map();
        this.handleNetworkFacet = (uid, facet, meta) => {
            if (this.isRoomHost) {
                if (facet.ownerId !== undefined && facet.destroyWhenOwnerDisconnect === true) {
                    if (!coreUserService.hasUserByClientIdx(facet.ownerId)) {
                        this.removeNetworkFacet(uid, meta.timeStamp);
                        this.connection.send(new DeleteModelMessage(uid));
                        return;
                    }
                }
            }
            let oldOwner;
            let entity = this.entities.get(uid);
            if (entity === undefined) {
                entity = {
                    uid,
                    updatedTime: meta.timeStamp,
                    facet
                };
                this.entities.set(uid, entity);
            }
            else {
                oldOwner = entity.facet.ownerId;
                entity.facet = facet;
            }
            this.ownerChanged(entity, entity.facet.ownerId, oldOwner);
        };
        this.handleRemoveNetworkFacet = (uid, _, meta) => {
            this.removeNetworkFacet(uid, meta.timeStamp);
        };
        this.handleRemoveSubModelMessage = (msg, meta) => {
            this.removeNetworkFacet(msg.uid, meta.timeStamp);
        };
        this.handleSessionDisconnectMessage = (msg, meta) => {
            if (!this.isRoomHost)
                return;
            this.removeNetworkFacetByClient(msg.clientIdx, meta.timeStamp);
        };
        this.handleClientReloadSessionMessage = (_msg) => {
            window.location.reload();
        };
        this.handleChangeRoomSupervisionMessage = (msg, meta) => {
            this.isRoomHost = msg.enable;
            if (msg.enable) {
                for (const peerIdx of this.peerIdxEntitiesMap.keys()) {
                    if (coreUserService.hasUserByClientIdx(peerIdx))
                        continue;
                    this.removeNetworkFacetByClient(peerIdx, meta.timeStamp);
                }
            }
        };
        this.handleServerTimeMessage = (msg, meta) => {
            this.avgTimeOffset = msg.avgServerTimeOffset + (Date.now() - meta.timeStamp);
        };
        this.connection.subscribeByFacetType(NetworkFacet, this.handleNetworkFacet);
        this.connection.subscribeByRemoveFacetType(NetworkFacet, this.handleRemoveNetworkFacet);
        this.connection.subscribeByType(RemoveSubModelMessage, this.handleRemoveSubModelMessage);
        this.connection.subscribeByType(ServerTimeMessage, this.handleServerTimeMessage);
        this.connection.subscribeByType(SessionDisconnectMessage, this.handleSessionDisconnectMessage);
        this.connection.subscribeByType(ChangeRoomSupervisionMessage, this.handleChangeRoomSupervisionMessage);
        this.connection.subscribeByType(ClientReloadSessionMessage, this.handleClientReloadSessionMessage);
    }
    subscribeOwnerChanged(uid, callback) {
        const existing = this.onOwnerChangedCallbacks.get(callback);
        if (existing === undefined || existing !== uid) {
            const entity = this.getNetworkEntity(uid);
            if ((entity === null || entity === void 0 ? void 0 : entity.facet.ownerId) !== undefined) {
                const isMine = entity.facet.ownerId === coreUserService.getLocalUser().clientIdx;
                callback(entity.uid, entity.facet.ownerId, isMine);
            }
        }
        this.onOwnerChangedCallbacks.set(callback, uid);
    }
    unsubscribeOwnerChanged(callback) {
        this.onOwnerChangedCallbacks.delete(callback);
    }
    createNetworkEntity(uid) {
        const entity = this.getNetworkEntity(uid);
        if (entity !== undefined)
            return false;
        const clientIdx = coreUserService.getLocalUser().clientIdx;
        const facet = new NetworkFacet(uid, clientIdx);
        const removeFacet = new RemoveFacetsOnDisconnectFacet([facet], clientIdx);
        this.entities.set(uid, {
            uid,
            updatedTime: Date.now(),
            facet
        });
        this.connection.sendMultiple([
            new UpdateFacetOnModelMessage(uid, facet),
            new UpdateFacetOnModelMessage(uid, removeFacet)
        ]);
        return true;
    }
    removeNetworkEntity(uid) {
        const entity = this.getNetworkEntity(uid);
        if (entity === undefined)
            return false;
        if (entity.facet.ownerId === coreUserService.getLocalUser().clientIdx) {
            console.warn("[network] Cannot remove network entity, you need to surrender ownership of your model first");
            return false;
        }
        if (entity.facet.ownerId !== undefined) {
            console.warn("[network] Cannot remove network entity, because its owned by another user");
            return false;
        }
        this.connection.sendMultiple([
            new RemoveFacetFromModelMessage(uid, NetworkFacet),
            new RemoveFacetFromModelMessage(uid, RemoveFacetsOnDisconnectFacet)
        ]);
        this.removeNetworkFacet(uid, Date.now());
        return true;
    }
    isNetworkEntity(uid) {
        return this.getNetworkEntity(uid) !== undefined;
    }
    isMine(uid) {
        const entity = this.getNetworkEntity(uid);
        if (entity === undefined)
            return false;
        return entity.facet.ownerId === coreUserService.getLocalUser().clientIdx;
    }
    controlledByOthers(uid) {
        const entity = this.getNetworkEntity(uid);
        if (entity === undefined)
            return false;
        if (entity.facet.ownerId === undefined)
            return false;
        if (entity.facet.ownerId === coreUserService.getLocalUser().clientIdx)
            return false;
        const user = coreUserService.getUserByClientIdx(entity.facet.ownerId);
        return user !== undefined;
    }
    takeOwnership(uid_1) {
        return __awaiter(this, arguments, void 0, function* (uid, destroyOnDisconnect = false) {
            const entity = this.getNetworkEntity(uid);
            if (entity === undefined) {
                console.warn(`takeOwnership: Can't find network entity with uid ${uid}.`);
                return false;
            }
            const localClientIdx = coreUserService.getLocalUser().clientIdx;
            const isMine = entity.facet.ownerId === localClientIdx;
            if (isMine)
                return false;
            const serverTime = this.getServerTime();
            if (entity.facet.ownerId !== undefined && entity.facet.lastOwnerTime !== undefined && entity.facet.lastOwnerTime >= serverTime) {
                console.warn(`takeOwnership: another was on earlier`, uid);
                return false;
            }
            const oldOwner = entity.facet.ownerId;
            entity.facet.ownerId = localClientIdx;
            entity.facet.lastOwnerTime = serverTime;
            entity.facet.destroyWhenOwnerDisconnect = destroyOnDisconnect;
            this.ownerChanged(entity, entity.facet.ownerId, oldOwner);
            this.connection.send(new UpdateFacetOnModelMessage(entity.uid, entity.facet));
            return true;
        });
    }
    surrenderOwnership(uid) {
        const entity = this.getNetworkEntity(uid);
        if (entity === undefined) {
            console.warn(`surrenderOwnership: Can't find network entity with uid ${uid}.`);
            return false;
        }
        const localClientIdx = coreUserService.getLocalUser().clientIdx;
        const isMine = entity.facet.ownerId === localClientIdx;
        if (!isMine)
            return false;
        this.ownerChanged(entity, undefined, entity.facet.ownerId);
        entity.facet.ownerId = undefined;
        this.connection.send(new UpdateFacetOnModelMessage(entity.uid, entity.facet));
        return true;
    }
    getNetworkEntity(uid) {
        var _a;
        const entity = this.entities.get(uid);
        if (entity !== undefined)
            return entity;
        let parentModel = (_a = modelPool.getModel(uid)) === null || _a === void 0 ? void 0 : _a.getParent();
        if (parent === undefined)
            return undefined;
        let parentEntity;
        while (parentModel !== undefined && !parentModel.isRoom() && !parentModel.isRoot() && parentEntity === undefined) {
            parentEntity = this.entities.get(parentModel.getUID());
            parentModel = parentModel.getParent();
        }
        return parentEntity;
    }
    getServerTime() {
        return new Date().getTime() + this.avgTimeOffset;
    }
    ownerChanged(entity, newOwner, oldOwner) {
        if (newOwner === oldOwner)
            return;
        if (oldOwner !== undefined) {
            const peerEntities = this.peerIdxEntitiesMap.get(oldOwner);
            if (peerEntities !== undefined) {
                peerEntities.delete(entity.uid);
            }
        }
        if (newOwner !== undefined) {
            let peerEntities = this.peerIdxEntitiesMap.get(newOwner);
            if (peerEntities === undefined) {
                peerEntities = new Set();
                this.peerIdxEntitiesMap.set(newOwner, peerEntities);
            }
            peerEntities.add(entity.uid);
        }
        const isMine = entity.facet.ownerId === coreUserService.getLocalUser().clientIdx;
        const model = modelPool.getModel(entity.uid);
        if (model === undefined)
            return;
        this.childrenOwnerChanged(newOwner, isMine, model);
    }
    childrenOwnerChanged(newOwner, isMine, model) {
        for (const [callback, uid] of this.onOwnerChangedCallbacks) {
            if (uid !== model.getUID())
                continue;
            callback(model.getUID(), newOwner, isMine);
        }
        for (const child of model.getChildren()) {
            this.childrenOwnerChanged(newOwner, isMine, child);
        }
    }
    removeNetworkFacet(uid, timestamp) {
        const entity = this.entities.get(uid);
        if (entity === undefined)
            return;
        if (entity.updatedTime > timestamp)
            return;
        this.ownerChanged(entity, undefined, entity.facet.ownerId);
        this.entities.delete(uid);
    }
    removeNetworkFacetByClient(clientIdx, timestamp) {
        const peerEntities = this.peerIdxEntitiesMap.get(clientIdx);
        if (peerEntities === undefined)
            return;
        const messages = [];
        for (const uid of peerEntities.values()) {
            const entity = this.entities.get(uid);
            if ((entity === null || entity === void 0 ? void 0 : entity.facet.destroyWhenOwnerDisconnect) !== true)
                continue;
            messages.push(new DeleteModelMessage(entity.uid));
            this.removeNetworkFacet(entity.uid, timestamp);
        }
        if (messages.length > 0)
            this.connection.sendMultiple(messages);
        this.peerIdxEntitiesMap.delete(clientIdx);
    }
}
export const networkEntitiesService = new NetworkEntitiesService();
